import type { BlockEditProps, BlockInstance } from "@wordpress/blocks";
import React, { useCallback, useEffect, ChangeEvent } from "react";
import {
  InnerBlocks,
  InspectorControls,
  useBlockProps,
  useInnerBlocksProps,
} from "@wordpress/block-editor";
import variations from "./variations";
import type { BlockAttributes } from "./block.json";
import type { BlockAttributes as ColBlockAttributes } from "../column/block.json";
import { select, useDispatch } from "@wordpress/data";
import type { BlockEditorSelectors } from "../../../source/hooks/useBlockEditorSelect";
import { PanelBody, PanelRow, ToggleControl } from "@wordpress/components";

const ALLOWED_BLOCKS = ["yellowtree/column"];

export type RowEditorProps = {};

const breakpoints = ["xs", "sm", "md", "lg", "xl", "xxl", "xxxl"] as const;
// All the keys for col that will be overwritten when changing the variation
const variationKeys = (breakpoints as readonly string[]).concat(
  breakpoints.map((x) => `offset${x[0]?.toUpperCase() ?? ""}${x.slice(1)}`)
);

export default function RowEditor(
  props: BlockEditProps<BlockAttributes>
): JSX.Element {
  const variation =
    variations.find((x) => x.name === props.attributes.template) ??
    variations[0];

  const innerBlocksProps = useInnerBlocksProps(
    {
      className:
        "row row-mb-fix" +
        (props.attributes.mobileReverse ? " flex-md-row-reverse" : ""),
    },
    {
      allowedBlocks: ALLOWED_BLOCKS,
      // @ts-ignore
      orientation: "horizontal",
      template: variation?.innerBlocks ?? [],
      templateLock: "all",
    }
  );

  const { updateBlockAttributes, replaceInnerBlocks } =
    useDispatch("core/block-editor");

  useEffect(() => {
    const innerBlocks = (
      select("core/block-editor") as BlockEditorSelectors
    ).getBlocks(props.clientId) as BlockInstance<ColBlockAttributes>[];
    if (!variation) {
      return;
    }
    for (
      let i = 0;
      i < Math.min(innerBlocks.length, variation.innerBlocks.length);
      ++i
    ) {
      const newAttribues: ColBlockAttributes = {
        ...(variation.innerBlocks[i]?.[1] ?? {}),
      };

      const switchAttributes: Array<{ attributes: Record<string, any> }> = [
        { attributes: {} },
        { attributes: {} },
      ];
      for (const [key, value] of Object.entries(
        innerBlocks[i]?.attributes ?? {}
      )) {
        if (variationKeys.includes(key)) {
          if (
            props.attributes.mobileReverse === true &&
            innerBlocks.length === 2
          ) {
            Object.assign(
              switchAttributes[0]?.attributes ?? {},
              innerBlocks[0]?.attributes
            );
            Object.assign(
              switchAttributes[1]?.attributes ?? {},
              innerBlocks[1]?.attributes ?? {}
            );
            if (switchAttributes[i]?.attributes) {
              (newAttribues as any)[key] = (switchAttributes[i]?.attributes ??
                ({} as Record<string, any>))[key];
            }
          }
        } else {
          (newAttribues as any)[key] = value;
        }
      }
      updateBlockAttributes(innerBlocks[i]?.clientId, newAttribues);
    }
  }, [
    props.clientId,
    variation?.innerBlocks ?? [],
    props.attributes.mobileReverse,
    updateBlockAttributes,
  ]);

  const {
    attributes: {
      mobileReverse = false,
      containerSizeSmall = false,
      containerSizeMedium = false,
      containerSize = "",
      section = false,
      deactivate = false,
      isFullHeight = false,
      isDoubleHeight = false,
      isFullWidthOnMobile = false,
    },
    setAttributes,
  } = props;

  const setDeactivate = useCallback(
    (deactivation: boolean) => {
      if (deactivation === deactivate) {
        return;
      }
      setAttributes({ deactivate: deactivation });
    },
    [deactivate, setAttributes]
  );

  const setMobileReverse = useCallback(
    (reverse: boolean) => {
      if (reverse === mobileReverse) {
        return;
      }
      setAttributes({ mobileReverse: reverse });

      const innerBlocks = (
        select("core/block-editor") as BlockEditorSelectors
      ).getBlocks(props.clientId) as BlockInstance<ColBlockAttributes>[];
      replaceInnerBlocks(props.clientId, [...innerBlocks].reverse());
    },
    [mobileReverse, props.clientId, replaceInnerBlocks, setAttributes]
  );

  const setContainerSizeSmall = useCallback(
    (small: boolean) => {
      if (small === containerSizeSmall) {
        return;
      }
      if (small) {
        setAttributes({ containerSizeMedium: false });
      }
      setAttributes({ containerSizeSmall: small });
      const innerBlocks = (
        select("core/block-editor") as BlockEditorSelectors
      ).getBlocks(props.clientId) as BlockInstance<ColBlockAttributes>[];
      for (const block of innerBlocks) {
        updateBlockAttributes(block.clientId, {
          containerSizeSmall: small,
        });
      }
    },
    [containerSizeSmall, props.clientId, setAttributes, updateBlockAttributes]
  );

  const setContainerSizeMedium = useCallback(
    (medium: boolean) => {
      if (medium === containerSizeMedium) {
        return;
      }
      if (medium) {
        setAttributes({ containerSizeSmall: false });
      }
      setAttributes({ containerSizeMedium: medium });
      const innerBlocks = (
        select("core/block-editor") as BlockEditorSelectors
      ).getBlocks(props.clientId) as BlockInstance<ColBlockAttributes>[];
      for (const block of innerBlocks) {
        updateBlockAttributes(block.clientId, {
          containerSizeMedium: medium,
          containerSizeSmall: false,
        });
      }
    },
    [containerSizeMedium, props.clientId, setAttributes, updateBlockAttributes]
  );

  const setContainerSize = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const size = event.target.value;
      if (size === containerSize) {
        return;
      }
      setAttributes({ containerSize: size });
      const innerBlocks = (
        select("core/block-editor") as BlockEditorSelectors
      ).getBlocks(props.clientId) as BlockInstance<ColBlockAttributes>[];
      for (const block of innerBlocks) {
        updateBlockAttributes(block.clientId, {
          containerSize: size,
        });
      }
    },
    [containerSize, props.clientId, setAttributes, updateBlockAttributes]
  );

  const setSection = useCallback(
    (tagName: boolean) => {
      if (tagName === section) {
        return;
      }
      setAttributes({ section: tagName });
    },
    [section, setAttributes]
  );

  const setFullHeight = useCallback(
    (height: boolean) => {
      if (height === isFullHeight) {
        return;
      }
      setAttributes({ isFullHeight: height });
    },
    [isFullHeight, setAttributes]
  );
  const setDoubleHeight = useCallback(
    (height: boolean) => {
      if (height === isDoubleHeight) {
        return;
      }
      setAttributes({ isDoubleHeight: height });
    },
    [isDoubleHeight, setAttributes]
  );
  const setFullWidthOnMobile = useCallback(
    (width: boolean) => {
      if (width === isFullWidthOnMobile) {
        return;
      }
      setAttributes({ isFullWidthOnMobile: width });
    },
    [isFullWidthOnMobile, setAttributes]
  );

  const outerClassNames: string[] = [
    "alignfull",
    "is-layout-constrained",
    "overflow-hidden",
  ];
  if (props.attributes.backgroundColor) {
    outerClassNames.push(`bg-${props.attributes.backgroundColor}`);
  }
  if (props.attributes.textAlignStartMobile) {
    outerClassNames.push("text-mobile-start");
  }
  if (props.attributes.isFullHeight) {
    outerClassNames.push("min-vh-100");
  }
  if (props.attributes.isDoubleHeight) {
    outerClassNames.push("min-vh-200");
  }
  if (props.attributes.isFullWidthOnMobile) {
    outerClassNames.push("full-width-on-mobile");
  }
  return (
    <>
      <InspectorControls key="deactive">
        <PanelBody title="Block ausblenden" initialOpen={false}>
          <PanelRow>
            <ToggleControl
              label="Block ausblenden"
              onChange={setDeactivate}
              checked={!!props.attributes.deactivate}
            />
          </PanelRow>
        </PanelBody>
      </InspectorControls>

      <InspectorControls key="responsive">
        <PanelBody title="Responsive" initialOpen={false}>
          <PanelRow>
            <ToggleControl
              label="Text auf Mobilgeräten links ausrichten"
              onChange={(checked) =>
                setAttributes({ textAlignStartMobile: checked })
              }
              checked={!!props.attributes.textAlignStartMobile}
            />
          </PanelRow>
        </PanelBody>
      </InspectorControls>
      <InspectorControls key="tag">
        <PanelBody title="Section Konfiguration" initialOpen={false}>
          <PanelRow>
            <ToggleControl
              label="Section-Tag verwenden"
              onChange={setSection}
              checked={!!props.attributes.section}
            />
          </PanelRow>
        </PanelBody>
      </InspectorControls>
      <InspectorControls key="advanced">
        <PanelBody title="Containergröße" initialOpen={false}>
          <PanelRow>
            <select onChange={setContainerSize} defaultValue={containerSize}>
              <option value="default">Standard</option>
              <option value="container container-75">schmal</option>
              <option value="container container-90">Medium</option>
              <option value="alignfull">Bildschirmbreite</option>
            </select>
          </PanelRow>
        </PanelBody>
      </InspectorControls>
      <InspectorControls key="fullHeight">
        <PanelBody title="Full Height" initialOpen={false}>
          <PanelRow>
            <ToggleControl
              label="Nimmt die komplette Bildschirmhöhe ein"
              onChange={setFullHeight}
              checked={!!props.attributes.isFullHeight}
            />
          </PanelRow>
        </PanelBody>
      </InspectorControls>
      <InspectorControls key="doubleHeight">
        <PanelBody title="Double Height" initialOpen={false}>
          <PanelRow>
            <ToggleControl
              label="Nimmt die 2-fache Bildschirmhöhe ein"
              onChange={setDoubleHeight}
              checked={!!props.attributes.isDoubleHeight}
            />
          </PanelRow>
        </PanelBody>
      </InspectorControls>
      <InspectorControls key="fullWidthOnMobile">
        <PanelBody title="Full Width On Mobile" initialOpen={false}>
          <PanelRow>
            <ToggleControl
              label="Nimmt auf mobile die komplette Bildschirmbreite ein"
              onChange={setFullWidthOnMobile}
              checked={!!props.attributes.isFullWidthOnMobile}
            />
          </PanelRow>
        </PanelBody>
      </InspectorControls>
      <div
        {...useBlockProps({
          className: outerClassNames.filter(Boolean).join(" "),
        })}
      >
        <div
          {...(props.attributes.containerSize
            ? { className: props.attributes.containerSize }
            : {})}
        >
          <div
            {...innerBlocksProps}
            {...(props.attributes.containerSizeSmall
              ? { className: "container container-75" }
              : {})}
            {...(props.attributes.containerSizeMedium
              ? { className: "container container-90" }
              : {})}
          />
        </div>
      </div>
    </>
  );
}
